<template>
  <layout>
    <template v-slot:header>
      <global-navigation />
    </template>

    <main v-if="hasPage">
      <flex-header>
        <template v-slot:flex>
          <button
            v-if="page.featuredimage.wares && hasVideo && !videoActive"
            :class="['media-wrap', 'playhead', { contain: page.featuredimage.uncropped }]"
            @click="videoActive = true"
            title="Watch Trailer"
          >
            <img :src="page.featuredimage.wares" />
          </button>
          <div
            v-else-if="page.featuredimage.wares && page.videofile && videoActive"
            class="media-wrap fade-in"
          >
            <video
              :src="videoFileUrl"
              :poster="page.featuredimage.wares"
              width="640"
              height="360"
              controls
              preload
              playsinline
              autoplay
              crossorigin="anonymous"
            >
              <track
                v-if="videoCaptionsUrl"
                :src="videoCaptionsUrl"
                kind="captions"
                default
              />
            </video>
          </div>
          <div
            v-else-if="page.featuredimage.wares && page.embed && videoActive"
            class="media-wrap"
          >
            <iframe
              :src="embedSrc"
              width="640"
              height="360"
              frameborder="0"
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen
            ></iframe>
          </div>
          <div
            v-else-if="page.featuredimage.wares"
            :class="['media-wrap', { contain: page.featuredimage.uncropped }]"
          >
            <img :src="page.featuredimage.wares" />
          </div>
        </template>
        <title-row
          :left="page.category"
          :center="page.title"
          :right="page.year"
          :title-is-h1="true"
        />
      </flex-header>

      <div
        :class="['content', { centered: !hasLeftColumn }]"
        v-if="page.text"
      >
        <hr v-if="page.featuredimage.wares" />
        <div
          class="content-left"
          v-if="hasLeftColumn"
        >
          <div class="content-left-inline">
            <figure v-if="page.posterimage && page.posterimage.poster">
              <img :src="page.posterimage.poster" />
            </figure>

            <ul
              v-if="page.watchlinkssingle"
              class="dropdown-single"
            >
              <li
                v-for="link in page.watchlinks"
                :key="link.link"
              >
                <a :href="link.link">{{ link.title }}</a>
              </li>
            </ul>
            <details
              v-else-if="page.watchlinks.length > 0"
              class="dropdown"
            >
              <summary>{{ page.watchlinkslabel || 'Watch Now' }}</summary>
              <ul>
                <li
                  v-for="link in page.watchlinks"
                  :key="link.link"
                >
                  <a :href="link.link">{{ link.title }}</a>
                </li>
              </ul>
            </details>
            <div
              v-else
              class="coming-soon"
            >
              Coming Soon
            </div>
          </div>
        </div>
        <rich-text
          class="content-right"
          :content="page.text"
        />
        <hr />
      </div>

      <gallery
        class="gallery"
        v-if="hasGallery"
      >
        <div
          class="slide"
          v-for="(block, i) in page.gallery"
          :key="i"
        >
          <vue-link
            :to="block.link"
            :class="['block', block._fieldset]"
          >
            <figure v-if="block.image && block.image.original">
              <img
                :src="block.image.original"
                :alt="block.image.alt"
              />
            </figure>
            <div
              v-if="block.title || block.text"
              class="caption"
            >
              <span class="title">{{ block.title }}</span> {{ block.text }}
            </div>
          </vue-link>
        </div>
      </gallery>
    </main>

    <template v-slot:footer>
      <global-footer
        v-if="hasPage"
        :border="hasGallery"
      >
        <vue-link to="/#productions">More Productions</vue-link>
      </global-footer>
      <div v-else></div>
    </template>
  </layout>
</template>

<script>
import PageMixin from '@/mixins/PageMixin'

import Gallery from '@/components/Gallery'
import TitleRow from '@/components/TitleRow'
import RichText from '@/components/RichText'
import FlexHeader from '@/components/FlexHeader'

export default {
  name: 'Production',
  mixins: [PageMixin],
  components: { Gallery, TitleRow, RichText, FlexHeader },
  data() {
    return {
      videoActive: false,
    }
  },
  computed: {
    hasWatchLinks() {
      return !!(this.page && this.page.watchlinks && this.page.watchlinks.length)
    },
    hasLeftColumn() {
      return !!(this.page && ((this.page.posterimage && this.page.posterimage.poster) || this.hasWatchLinks))
    },
    hasGallery() {
      return !!(this.page && this.page.gallery && this.page.gallery.length)
    },
    videoFileUrl() {
      return this.page.videofile
    },
    videoCaptionsUrl() {
      return this.page.videocaptions
    },
    embedSrc() {
      if (this.page && this.page.embed) {
        if (this.page.embed.type === 'vimeo') {
          return `https://player.vimeo.com/video/${this.page.embed.id}?autoplay=1`
        }
        if (this.page.embed.type === 'youtube') {
          return `https://youtube.com/embed/${this.page.embed.id}?autoplay=1`
        }
      }
      return false
    },
    hasVideo() {
      return this.page && (this.page.videofile || this.page.embed)
    },
  },
  mounted() {
    setTimeout(() => {
      console.log(this.page)
    }, 1000)
  },
}
</script>

<style scoped>
/**
 * Container
 */

main {
  margin-bottom: 4rem;
}

main > * + * {
  margin-top: 4rem;
}

main > *:nth-child(2) {
  margin-top: 0;
}

/**
 * Grid
 */

.content {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  padding-left: 2rem;
  padding-right: 2rem;
  grid-gap: 4rem 2rem;
}

.content-left {
  grid-column: 2 / 8;
}

.content-right {
  grid-column: 10 / 24;
}

.content.centered .content-right {
  grid-column: 6 / 20;
}

.content hr {
  margin: 0;
  grid-column: 1 / -1;
}

.content hr:first-child {
  margin-top: 0.5rem;
}

/**
 * Media
 */

.media-wrap {
  width: 100%;
  height: 100%;
  position: relative;
}

button.media-wrap {
  -webkit-appearance: none;
  -moz-appearance: none;
  border: none;
  background: none;
  cursor: pointer;
}

button.media-wrap.focus-visible {
  border: 1px dashed #fff;
}

.media-wrap video {
  position: absolute;
  top: 10%;
  left: 10%;
  height: 80%;
  width: 80%;
}

.media-wrap iframe {
  position: absolute;
  top: 10%;
  left: 10%;
  height: 80%;
  width: 80%;
}

.media-wrap img {
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0.8;
}

.media-wrap.contain img {
  object-fit: contain;
}

.media-wrap.playhead::after {
  content: 'Watch Trailer';
  font: var(--nord);
  text-transform: uppercase;
  width: 20rem;
  height: 14rem;
  background-image: url(~@/assets/playhead.svg);
  background-size: 8.5rem;
  background-repeat: no-repeat;
  background-position: top center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  text-align: center;
  color: #fff;
}

/**
 * Content Left
 */

.content-left {
  text-align: center;
}

.content-left-inline {
  display: inline-block;
}

.content-left figure {
  margin-bottom: 2rem;
}

.content-left img {
  display: block;
  height: auto;
  width: auto;
  max-height: 60vh;
  max-width: 100%;
  margin: 0 auto;
}

details {
  min-width: 20rem;
  max-width: 36rem;
  margin: 0 auto;
  text-transform: uppercase;
  background: #000;
  text-align: left;
  border: 1px dashed currentColor;
}

details.coming-soon summary {
  border: none;
  font: var(--nord);
  display: flex;
  justify-content: space-between;
  line-height: 1;
  list-style-image: none;
  padding: var(--p1);
}

div.coming-soon {
  min-width: 20rem;
  max-width: 36rem;
  margin: 0 auto;
  text-transform: uppercase;
  background: #000;
  text-align: left;
  border: 1px dashed currentColor;
  font: var(--nord);
  padding: var(--p1);
}

details ul {
  font: var(--nord);
  border-top: 1px dashed currentColor;
}

details li {
  padding-left: 1em;
  text-indent: -1em;
  padding-bottom: 0.7rem;
}

details li:last-child {
  padding-bottom: 0rem;
}

.dropdown-single {
  min-width: 20rem;
  max-width: 36rem;
  margin: 0 auto;
  font: var(--nord);
  text-transform: uppercase;
  border: 1px dashed currentColor;
  padding: var(--p1);
}

/**
 * Gallery
 */

.gallery {
  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.gallery::-webkit-scrollbar {
  width: 0px;
  height: 0px;
  background: transparent;
}

.slide {
  display: inline-block;
  white-space: normal;
}

.slide:last-child {
  padding-right: 2rem;
}

.block {
  border-radius: var(--br);
  position: relative;
  margin-left: 2rem;
  display: block;
}

.block.image_entry img {
  border-radius: var(--br);
  height: 38rem;
  width: auto;
}

.block.press_entry,
.block.editorial {
  border: 1px dashed #fff;
}

.block.press_entry,
.block.editorial {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
}

.block.press_entry figure,
.block.editorial figure {
  margin: 2rem;
  display: block;
  flex: 1;
  position: relative;
  width: 100%;
}

.block.press_entry img,
.block.editorial img {
  display: block;
  object-fit: contain;
  position: absolute;
  height: 100%;
  width: 100%;
}

.block.press_entry .caption,
.block.editorial .caption {
  flex-shrink: 0;
  text-align: center;
  width: 100%;
}

.block.press_entry .caption,
.block.editorial .caption {
  font-size: 2rem;
}

.block.press_entry .caption .title,
.block.editorial .caption .title {
  font: var(--nord);
  text-transform: uppercase;
  margin-right: 0.75rem;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in {
  opacity: 0;
  animation: fade-in 0.5s forwards;
}

/**
 * Media Queries
 */

@media (max-width: 1250px) {
  .content-right {
    grid-column: 9 / 24;
  }
  .content.centered .content-right {
    grid-column: 5 / 21;
  }
}

@media (max-width: 1190px) {
  .content hr {
    grid-column: 1 / -1;
  }
  .content-left {
    grid-column: 1 / 9;
  }
  .content-right {
    grid-column: 10 / -1;
  }
  .content.centered .content-right {
    grid-column: 4 / 22;
  }
}

@media (max-width: 1000px) {
  /**
   * Grid
   */

  .content {
    display: flex;
    flex-direction: column;
  }

  .content > *:not(:last-child) {
    margin-bottom: 4rem !important;
  }

  .content-left {
    order: 1;
  }
  .content hr:last-child {
    order: 2;
  }

  /**
   * Media
   */

  .media-wrap.playhead::after {
    content: '';
    height: 6.5rem;
    background-size: contain;
  }

  .media-wrap iframe {
    width: 90%;
    left: 5%;
  }

  /**
   * Gallery
   */

  .block {
    /* width: calc(80vw);
    height: calc((80vw) * 0.66); */
  }

  .block.image_entry img {
    height: 20rem;
  }

  .block.press_entry figure,
  .block.editorial figure {
    display: none;
  }
}
</style>
